import type { RegisteredParticipant } from '@/state/stores/registrationStore';

/**
 * Shared option values + i18n keys under `strings.participantFieldOptions`.
 * Used by participant edit modal, registration flow, profile registration, partner fields.
 */
export const PARTICIPATION_MODE_OPTIONS = [
  {
    value: false as const,
    labelKey: 'modeFestivalLabel',
    descKey: 'modeFestivalDesc',
  },
  {
    value: true as const,
    labelKey: 'modeChampionshipLabel',
    descKey: 'modeChampionshipDesc',
  },
] as const;

export const ROLE_SELECT_OPTIONS = [
  { value: 'leader' as const, labelKey: 'roleLeader' },
  { value: 'follower' as const, labelKey: 'roleFollower' },
  { value: 'switch' as const, labelKey: 'roleSwitch' },
] as const;

export const LEVEL_SELECT_OPTIONS = [
  { value: 'beginner' as const, labelKey: 'levelBeginner' },
  { value: 'intermediate' as const, labelKey: 'levelIntermediate' },
  { value: 'advanced' as const, labelKey: 'levelAdvanced' },
  { value: 'professional' as const, labelKey: 'levelProfessional' },
] as const;

export const CATEGORY_SELECT_OPTIONS = [
  { value: 'pro' as const, labelKey: 'categoryPro' },
  { value: 'amateur' as const, labelKey: 'categoryAmateur' },
  { value: 'senior' as const, labelKey: 'categorySenior' },
] as const;

export type ChampionshipCategoryUi =
  (typeof CATEGORY_SELECT_OPTIONS)[number]['value'];

/** Strip deprecated `junior` and unknown values so form/store stay aligned with UI options. */
export function normalizeChampionshipCategoryForUi(
  raw: string | undefined | null,
): ChampionshipCategoryUi | '' {
  const c = String(raw ?? '').trim();
  if (c === 'pro' || c === 'amateur' || c === 'senior') return c;
  return '';
}

/** `useTranslations('strings.participantFieldOptions')` */
export type ParticipantFieldOptionTranslate = (key: string) => string;

export function resolveParticipationModeOptions(t: ParticipantFieldOptionTranslate) {
  return PARTICIPATION_MODE_OPTIONS.map((row) => ({
    value: row.value,
    label: t(row.labelKey),
    desc: t(row.descKey),
  }));
}

export function resolveRoleSelectOptions(t: ParticipantFieldOptionTranslate) {
  return ROLE_SELECT_OPTIONS.map((row) => ({
    value: row.value,
    label: t(row.labelKey),
  }));
}

export function resolveLevelSelectOptions(t: ParticipantFieldOptionTranslate) {
  return LEVEL_SELECT_OPTIONS.map((row) => ({
    value: row.value,
    label: t(row.labelKey),
  }));
}

export function resolveCategorySelectOptions(t: ParticipantFieldOptionTranslate) {
  return CATEGORY_SELECT_OPTIONS.map((row) => ({
    value: row.value,
    label: t(row.labelKey),
  }));
}

/** Short role label for cards / lists (leader | follower | switch). */
export function participantRoleShortLabel(
  role: RegisteredParticipant['role'] | '' | undefined,
  t: ParticipantFieldOptionTranslate,
): string {
  if (role === 'leader') return t('roleLeader');
  if (role === 'follower') return t('roleFollower');
  if (role === 'switch') return t('roleSwitch');
  return '';
}
